import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EconomicsPlanNode } from '../../types/economicsTypes';
import { EconomicsCheckbox } from './EconomicsCheckbox';
import { EconomicsPatientRow } from './EconomicsPatientRow';
import { formatDkk } from '../../utils/currencyUtils';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import styles from './Economics.module.css';

interface EconomicsPlanRowProps {
  node: EconomicsPlanNode;
  canWrite: boolean;
  onToggleKeys: (keys: string[], completed: boolean) => void;
  onToggleLeaf: (key: string, completed: boolean) => void;
  onToggleClaim: (key: string, claimed: boolean) => void;
  onOverrideChange: (key: string, value: number | null, note: string | null) => void;
}

export const EconomicsPlanRow = ({
  node,
  canWrite,
  onToggleKeys,
  onToggleLeaf,
  onToggleClaim,
  onOverrideChange
}: EconomicsPlanRowProps) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const keys = node.patients.flatMap((patient) => patient.groups.map((group) => group.key));

  return (
    <>
      <div className={`${styles.row} ${styles.rowPlan}`}>
        <button
          type="button"
          className={styles.expandButton}
          onClick={() => setIsOpen((open) => !open)}
          aria-label={t('economics-toggle-expand')}
          aria-expanded={isOpen}
        >
          <ChevronIcon className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} />
        </button>
        <EconomicsCheckbox
          state={node.selectionState}
          onChange={(next) => onToggleKeys(keys, next)}
          disabled={!canWrite}
          ariaLabel={t('economics-complete-plan', { plan: node.planName })}
        />
        <div className={styles.label}>
          <span>{node.planName}</span>
        </div>
        <div className={styles.spread}>
          <span className={styles.subtotal}>{formatDkk(node.total, i18n.language)}</span>
        </div>
      </div>
      {isOpen &&
        node.patients.map((patient) => (
          <EconomicsPatientRow
            key={patient.patientId}
            node={patient}
            canWrite={canWrite}
            onToggleKeys={onToggleKeys}
            onToggleLeaf={onToggleLeaf}
            onToggleClaim={onToggleClaim}
            onOverrideChange={onOverrideChange}
          />
        ))}
    </>
  );
};
